import type { Signal } from '@preact/signals-react';
import { DelayedRender } from '@visiba/react-std';
import { ReactNode } from 'react';

type AsyncSignalValueIdle = {
	value: null;
	flowState: 'idle';
};

type AsyncSignalValuePending = {
	value: null;
	flowState: 'pending';
};

type AsyncSignalValueError<E> = {
	value: E;
	flowState: 'error';
};

type AsyncSignalValueAccessible<T> = {
	value: T;
	flowState: 'accessible';
};

export type AsyncSignalValue<T, E = null> =
	| AsyncSignalValueIdle
	| AsyncSignalValuePending
	| AsyncSignalValueError<E>
	| AsyncSignalValueAccessible<T>;

interface Props<T, E> {
	derive: Signal<AsyncSignalValue<T, E>>;
	fallback?: ReactNode;
	catch?: ((error: E) => ReactNode) | ReactNode;
	children?: ((data: T) => ReactNode) | ReactNode;
	fallbackDelay?: number;
}

export function Async<T, E>({ fallbackDelay, derive, fallback, catch: $catch, children }: Props<T, E>): JSX.Element | null {
	if (derive.value.flowState === 'idle') {
		return null;
	}

	if (derive.value.flowState === 'pending') {
		return fallback ? <DelayedRender delay={fallbackDelay}>{fallback}</DelayedRender> : null;
	}

	if (derive.value.flowState === 'error') {
		return $catch ? <>{typeof $catch === 'function' ? $catch(derive.value.value) : $catch}</> : null;
	}

	return <>{typeof children === 'function' ? children(derive.value.value) : children}</>;
}

export function isAsyncSignalAccessible<T>(value: Signal<AsyncSignalValue<T>>): value is Signal<AsyncSignalValueAccessible<T>> {
	return value.value.flowState === 'accessible';
}
